import React, { useCallback, useEffect, useState } from 'react';
import ResponseShell from './ResponseShell';
import FormControl from '../../common/FormControl';
import { useAutosave } from './useAutosave';
import { type ProcedureResponseProps, computeResponseReadOnly } from './responseShellTypes';
import {
  responseService,
  transitionService,
  procedureInstanceService,
  ratingTemplateService,
  ratingOptionService,
  ratingResponseService,
  findingService,
  procedureEvidenceService,
  type RatingTemplateItem,
  type RatingOptionItem,
  type Finding,
  type ProcedureEvidence,
} from '../../../services/auditService';
import { useAuth } from '../../../contexts/AuthContext';
import ObservationModal, { type ObservationModalRowContext } from './ObservationModal';
import RaiseFindingButton, { ITEM_TYPE_RATING_RESPONSE } from './RaiseFindingButton';
import InlineQuestionMeta from './InlineQuestionMeta';
import EvidenceCell from './EvidenceCell';
import RejectNoteCell from './RejectNoteCell';

/**
 * Plan 05b-03b — sample-less Rating response body.
 *
 * Per CONTEXT D-01 + D-03 (Phase 05b), the Rating procedure type does NOT
 * need a population/sample picker — every rating question in the procedure's
 * rating templates is scored exactly once per procedure_instance.
 *
 * Backend contract (verified against backend/routes/adt_routes.py +
 * adt_schemas.ADTRatingResponseBase):
 *
 *   - POST /adt/rating-responses/init/{procedure_instance_id}
 *         Seeds one row per active rating_template for the procedure. Idempotent.
 *   - GET  /adt/rating-responses/by-instance/{procedure_instance_id}
 *         Returns existing rating_responses rows.
 *   - PUT  /adt/rating-responses/{id}
 *         body: { rating_option_id?, score_obtained?, modified_by }
 *
 * IMPORTANT — schema deviation from PLAN.md <interfaces>:
 *   The plan listed `rating_option_id: int` + `score: Decimal`. The actual
 *   schema (verified) uses `score_obtained: int` (per ADTRatingResponseBase).
 *   No POST endpoint exists for ad-hoc create — rows are seeded server-side
 *   via init/{id}. This component follows the actual schema (Rule 1 — bug
 *   deviation; Rule 3 — blocking issue).
 *
 * UI model (one row per rating template question):
 *   - Question text (RatingTemplateItem.rating_question)
 *   - Option dropdown (RatingOptionItem.rating_option_text)
 *   - Score (read-only, DERIVED from the selected option's rating_option_score;
 *     the auditor never types it — picking an option sets it, deselecting clears it)
 */

interface RatingRow {
  /** ADT_rating_responses.id (always present after init). */
  response_id: number;
  /** ADT_rating_templates.id — the question. */
  rating_template_id: number;
  /** Display: the question text from the template. */
  question_text: string;
  /** Maximum allowed score (from template). Default 10. */
  maximum_score: number;
  /** Available options for this question. */
  options: RatingOptionItem[];
  /** Currently selected rating_option_id (or null). */
  rating_option_id: number | null;
  /** Score as string for the input; converted to int on save. */
  score: string;
  /** G2 — checker per-line reject note (non-empty => line rejected). */
  reject_note: string;
  dirty: boolean;
}

const RatingResponse: React.FC<ProcedureResponseProps> = ({
  instance,
  role,
  performerName,
  approverName,
  onSaved,
  readOnly: readOnlyProp,
}) => {
  const { state } = useAuth();
  const entityId = state.user?.entity_id ?? 0;
  const currentUserId = state.user?.id ?? 0;
  // G3 — an Approved instance is immutable. Derive from workflow_state unless the
  // Read-only unless the maker (performer) is editing an open instance — shared rule.
  const readOnly = computeResponseReadOnly(instance, role, readOnlyProp);

  const [rows, setRows] = useState<RatingRow[]>([]);
  const [templateMissing, setTemplateMissing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Plan 05b-05 / STAFF-06 — inline finding raise state.
  const [findings, setFindings] = useState<Finding[]>([]);
  const [modalRow, setModalRow] = useState<{
    context: ObservationModalRowContext;
    itemRecordId: number | null;
  } | null>(null);
  // Phase 113 REQ-8 — per-row evidence, fetched once and grouped.
  const [evidenceByRow, setEvidenceByRow] = useState<Map<string, ProcedureEvidence[]>>(new Map());

  const reloadFindings = useCallback(async () => {
    try {
      const list = await findingService.listForProcedure(instance.id);
      setFindings(list);
    } catch {
      // Non-fatal.
    }
  }, [instance.id]);

  const reloadEvidence = useCallback(async () => {
    try {
      const list = await procedureEvidenceService.list(instance.id);
      const map = new Map<string, ProcedureEvidence[]>();
      for (const ev of list) {
        if (ev.item_type_id != null && ev.item_record_id != null) {
          const key = `${ev.item_type_id}-${ev.item_record_id}`;
          const bucket = map.get(key) ?? [];
          bucket.push(ev);
          map.set(key, bucket);
        }
      }
      setEvidenceByRow(map);
    } catch {
      // Non-fatal.
    }
  }, [instance.id]);

  useEffect(() => {
    reloadFindings();
    reloadEvidence();
  }, [reloadFindings, reloadEvidence]);

  const findingsForRow = useCallback(
    (itemRecordId: number | null): Finding[] => {
      if (itemRecordId == null) return [];
      return findings.filter(
        (f) =>
          f.item_type_id === ITEM_TYPE_RATING_RESPONSE &&
          f.item_record_id === itemRecordId,
      );
    },
    [findings],
  );

  // Initial load: fetch template + options, seed responses, merge.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 1. Discover the procedure_template_id for this instance.
        const inst = await procedureInstanceService.get(instance.id);
        const templateId = inst.procedure_template_id ?? null;
        if (!templateId) {
          if (!cancelled) setTemplateMissing(true);
          return;
        }

        // 2. Fetch the rating templates (questions) for this procedure.
        let ratingTemplates: RatingTemplateItem[] = [];
        try {
          ratingTemplates = await ratingTemplateService.listForProcedure(templateId);
        } catch {
          // Fall through to "templateMissing" if the API rejects.
        }
        if (ratingTemplates.length === 0) {
          if (!cancelled) setTemplateMissing(true);
          return;
        }

        // 3. Fetch ALL rating options once, then group by rating_template_id.
        //    listAll() is server-side limited to 500 — sufficient for the
        //    handful of options per question this UI surfaces.
        const allOptions = await ratingOptionService.listAll();
        const optionsByTemplate = new Map<number, RatingOptionItem[]>();
        for (const opt of allOptions) {
          const list = optionsByTemplate.get(opt.rating_template_id) ?? [];
          list.push(opt);
          optionsByTemplate.set(opt.rating_template_id, list);
        }

        // 4. Seed rating_responses (idempotent — server skips existing rows),
        //    then fetch the current set so each row gets a response_id.
        try {
          await ratingResponseService.init(instance.id);
        } catch {
          // Init failures are non-fatal — listByInstance below may still find
          // pre-existing rows; if not, the "Awaiting template" notice fires.
        }
        const responses = await ratingResponseService.listByInstance(instance.id);
        const responseByTemplate = new Map<number, typeof responses[number]>();
        for (const r of responses) {
          if (r.rating_template_id != null) {
            responseByTemplate.set(r.rating_template_id, r);
          }
        }

        // 5. Build one row per rating template question (skip questions that
        //    failed to seed — they would have no response_id and the UI can't
        //    PUT without an id).
        const merged: RatingRow[] = [];
        for (const tmpl of ratingTemplates) {
          const resp = responseByTemplate.get(tmpl.id);
          if (!resp) continue;
          merged.push({
            response_id: resp.id,
            rating_template_id: tmpl.id,
            question_text: tmpl.rating_question,
            maximum_score: tmpl.maximum_score ?? 10,
            options: optionsByTemplate.get(tmpl.id) ?? [],
            rating_option_id: resp.rating_option_id ?? null,
            score: resp.score_obtained != null ? String(resp.score_obtained) : '',
            reject_note: (resp as any).reject_note ?? '',
            dirty: false,
          });
        }

        if (!cancelled) {
          setRows(merged);
          if (merged.length === 0) setTemplateMissing(true);
        }
      } catch (e: any) {
        if (!cancelled) {
          setLoadError(e?.message ?? 'Failed to load rating template.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [instance.id]);

  // Save: PUT each dirty row, clear flag.
  const save = useCallback(async () => {
    const dirty = rows.filter((r) => r.dirty);
    if (dirty.length === 0) return;

    for (const r of dirty) {
      // Convert score to int; clamp to [0, maximum_score]. Empty string -> 0.
      const raw = r.score.trim();
      const parsed = raw === '' ? 0 : Number(raw);
      const score = Number.isFinite(parsed)
        ? Math.max(0, Math.min(r.maximum_score, Math.trunc(parsed)))
        : 0;
      await responseService.update('Rating', r.response_id, {
        rating_option_id: r.rating_option_id,
        score_obtained: score,
        modified_by: currentUserId,
      });
    }
    setRows((prev) => prev.map((r) => ({ ...r, dirty: false })));
  }, [rows, currentUserId]);

  const { status, markDirty, flush } = useAutosave(save, 1000);

  const updateOption = (idx: number, optionId: number | null) => {
    setRows((prev) => {
      const next = [...prev];
      const row = next[idx];
      // When the user picks an option, default the score to the option's
      // rating_option_score — they can still override the input below.
      const opt = optionId != null ? row.options.find((o) => o.id === optionId) : undefined;
      next[idx] = {
        ...row,
        rating_option_id: optionId,
        // Score is DERIVED from the option and shown read-only — set it from the
        // picked option, clear it when the option is deselected.
        score: opt ? String(opt.rating_option_score) : '',
        dirty: true,
      };
      return next;
    });
    markDirty();
  };

  // G2 — number of lines a checker has rejected (drives the shell approve-gate).
  const rejectedCount = rows.filter(r => r.reject_note && r.reject_note.trim()).length;

  // Submit gate (2026-06-29) — every item must have a rating option picked before
  // submit. rating_option_id == null => not yet rated.
  const unrated = rows.filter(r => r.rating_option_id == null).length;
  const incompleteReason =
    rows.length > 0 && unrated > 0
      ? `Rate all ${rows.length} items before submitting — ${unrated} still unrated.`
      : null;

  return (
    <ResponseShell
      instance={instance}
      procedureType="Rating"
      role={role}
      performerName={performerName}
      approverName={approverName}
      autosaveStatus={status}
      rejectedCount={rejectedCount}
      incompleteReason={incompleteReason}
      onSaveDraft={async () => {
        await flush();
      }}
      onSubmit={async () => {
        await flush();
        await transitionService.submit('procedure', instance.id);
        onSaved?.();
      }}
      onApprove={async () => {
        await transitionService.approve('procedure', instance.id);
        onSaved?.();
      }}
      onReject={async (comment) => {
        await transitionService.reject('procedure', instance.id, comment);
        onSaved?.();
      }}
      onReopen={async () => {
        await transitionService.reopen('procedure', instance.id);
        onSaved?.();
      }}
    >
      {loadError && (
        <div className="response-load-error">{loadError}</div>
      )}
      {templateMissing && rows.length === 0 && (
        <div className="response-empty-warning">
          No rating template attached to this procedure. Ask your DRC admin to
          add rating questions before scoring.
        </div>
      )}
      <div className="response-section">
        <div className="table-list response-qtable">
          <div className="table-list-scroll">
            <table className="table-list-table">
              <colgroup>
                <col className="response-col-sno" />
                <col />
                <col className="response-col-answer" />
                <col className="response-col-sno" />
              </colgroup>
              <thead>
                <tr>
                  <th className="table-list-th response-th-sno">S.No</th>
                  <th className="table-list-th">Question</th>
                  <th className="table-list-th">Rating</th>
                  <th className="table-list-th">Score</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const rowFindings = findingsForRow(r.response_id);
                  const rowEvidence = evidenceByRow.get(`${ITEM_TYPE_RATING_RESPONSE}-${r.response_id}`) ?? [];
                  const selectedOption = r.options.find((o) => o.id === r.rating_option_id);
                  const responseValue = selectedOption
                    ? `${selectedOption.rating_option_text} (score ${r.score})`
                    : r.score
                      ? `Score ${r.score}`
                      : null;
                  return (
                    <React.Fragment key={r.response_id}>
                      <tr className="table-list-row">
                        <td className="table-list-cell response-cell-sno">{idx + 1}</td>
                        <td className="table-list-cell response-cell-question">
                          {/* REQ-7: question text LEFT, action cluster RIGHT, same flex row. */}
                          <div className="response-question-row">
                            <span className="response-question-text">{r.question_text}</span>
                            {/* Rating has no remark field — no remarks button. */}
                            <div className="response-action-cluster response-action-cluster--inline">
                              <EvidenceCell
                                scope="row"
                                evidenceType={undefined}
                                entityId={entityId}
                                createdBy={currentUserId}
                                procedureInstanceId={instance.id}
                                itemTypeId={ITEM_TYPE_RATING_RESPONSE}
                                itemRecordId={r.response_id}
                                questionLabel={r.question_text}
                                disabled={readOnly}
                                showCount={false}
                                onChanged={reloadEvidence}
                              />
                              <RejectNoteCell
                                responseId={r.response_id}
                                rejectNote={r.reject_note}
                                procedureType="Rating"
                                role={role}
                                readOnly={readOnly}
                                onSaved={(note) =>
                                  setRows(prev => prev.map((x) => (x.response_id === r.response_id ? { ...x, reject_note: note } : x)))
                                }
                              />
                              <RaiseFindingButton
                                count={rowFindings.length}
                                showCount={false}
                                disabled={readOnly}
                                aria-label="Raise finding"
                                title="Raise a finding from this row"
                                onClick={() => {
                                  setModalRow({
                                    context: {
                                      procedureName: instance.audit_procedure_name,
                                      sampleLabel: r.question_text,
                                      responseValue,
                                    },
                                    itemRecordId: r.response_id,
                                  });
                                }}
                              />
                            </div>
                          </div>
                          {/* REQ-8 rev2: stacked Remarks / Attachments / Findings areas. */}
                          <InlineQuestionMeta
                            evidence={rowEvidence}
                            findings={rowFindings}
                            readOnly={readOnly}
                            onEvidenceRemoved={reloadEvidence}
                          />
                        </td>
                        <td className="table-list-cell">
                          {/* Plain native dropdown — only a handful of rating options, no search needed. */}
                          <FormControl.Select
                            label=""
                            data-testid={`SEL_rating_response_${r.response_id}_option`}
                            value={r.rating_option_id == null ? '' : String(r.rating_option_id)}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                              updateOption(idx, e.target.value === '' ? null : Number(e.target.value))
                            }
                            noMargin
                            disabled={readOnly}
                          >
                            <option value="">Select rating…</option>
                            {r.options.map((opt) => (
                              <option key={opt.id} value={String(opt.id)}>{opt.rating_option_text}</option>
                            ))}
                          </FormControl.Select>
                        </td>
                        <td className="table-list-cell">
                          {/* Score is DERIVED from the selected rating option and is
                              never directly editable — always disabled. */}
                          <FormControl.Input
                            label=""
                            type="number"
                            data-testid={`INP_rating_response_${r.response_id}_score`}
                            value={r.score}
                            onChange={() => { /* derived from option — no manual edit */ }}
                            noMargin
                            disabled
                          />
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
                {rows.length === 0 && !templateMissing && !loadError && (
                  <tr>
                    <td className="table-list-cell modal-empty-text" colSpan={4}>
                      Loading…
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Plan 05b-05 / STAFF-06 — single ObservationModal + FindingDetailModal */}
      <ObservationModal
        isOpen={modalRow !== null}
        onClose={() => setModalRow(null)}
        procedureInstanceId={instance.id}
        entityId={entityId}
        currentUserId={currentUserId}
        rowContext={modalRow?.context ?? { procedureName: instance.audit_procedure_name }}
        itemTypeId={ITEM_TYPE_RATING_RESPONSE}
        itemRecordId={modalRow?.itemRecordId ?? null}
        onCreated={(f) => {
          setFindings((prev) => [...prev, f]);
          reloadFindings();
        }}
      />
    </ResponseShell>
  );
};

export default RatingResponse;
